import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import {
  Box,
  CountUp,
  formatAmount,
  Icon,
  Skeleton,
  Text,
  Tooltip,
  useStyle,
} from '@betty/moonlight';
import {
  StackNavigationProp,
  useFocusEffect,
  useNavigation,
} from '@betty/navigation';
import { usePrevious } from '@betty/toolkit/hooks';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import {
  getCoinBalance,
  selectCoinBalance,
  selectCoinBalanceDataState,
} from '../state/coinBalance/coinBalance.slice';

const staticStyles = {
  height: 40,
  variant: 'default',
  width: 'auto',
  backgroundColor: 'secondaryBackgroundDefault',
  borderRadius: 'radius-200',
  padding: 'space-300',
  gap: 'space-100',
  flexDirection: 'column',
  justifyContent: 'center',
} as const;

const MAX_CAPTION_BALANCE = 99999999;

export const coinBalanceWallet: { current: View | null } = {
  current: null,
};

function CoinBalanceHeaderItem() {
  const dispatch = useDispatch();
  const coinBalance = useSelector(selectCoinBalance);
  const coinBalanceDataState = useSelector(selectCoinBalanceDataState);
  const coinBalanceRef = useRef<View>(null);

  const pressableStyle = useStyle({ ...staticStyles });
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const [initialBalance, setInitialBalance] = useState<number>();
  const prevCoinBalance = usePrevious(coinBalance);

  const balanceDiff = useMemo(() => {
    if (prevCoinBalance || (initialBalance === 0 && prevCoinBalance === 0)) {
      return coinBalance - prevCoinBalance;
    } else {
      return 0;
    }
  }, [coinBalance, initialBalance, prevCoinBalance]);

  useEffect(() => {
    const prevBalance = prevCoinBalance || 0;

    if (
      (initialBalance === undefined && coinBalanceDataState === 'fulfilled') ||
      coinBalance < prevBalance
    ) {
      setInitialBalance(coinBalance);
    }
  }, [coinBalance, coinBalanceDataState, initialBalance, prevCoinBalance]);

  const coins = useMemo(
    () =>
      coinBalanceDataState?.match('fulfilled|refreshing') ? (
        <CountUp
          variant={coinBalance > MAX_CAPTION_BALANCE ? 'badge' : 'caption'}
          color="darkSurfacePrimary"
          start={initialBalance ?? 0}
          useVisualIncrement={true}
          useFrameRestriction={true}
          end={coinBalance}
          increment={coinBalance - (initialBalance ?? 0)}
          visualIncrement={1}
          duration={2000}
          formatValue={formatAmount}
        />
      ) : (
        <Skeleton width={30} />
      ),
    [coinBalance, coinBalanceDataState, initialBalance],
  );

  const showTooltip = useMemo(
    () => balanceDiff > 0 && balanceDiff !== coinBalance,
    [balanceDiff, coinBalance],
  );

  const handleClick = () => {
    navigation.navigate('Shop');
  };

  useFocusEffect(
    useCallback(() => {
      dispatch(getCoinBalance());
    }, [dispatch]),
  );

  useFocusEffect(
    useCallback(() => {
      coinBalanceWallet.current = coinBalanceRef.current;
    }, []),
  );

  return (
    <Pressable
      ref={coinBalanceRef}
      style={pressableStyle}
      onPress={handleClick}
    >
      <Box
        flexDirection="row"
        alignItems="center"
        gap="space-200"
        position="relative"
      >
        <Icon
          name="bettyCoin"
          gradient="accentBackground"
          width="space-400"
          height="space-400"
        />
        {coins}
        {showTooltip && (
          <Tooltip>
            <Text variant="caption-semi" color="accentSurfacePrimary">
              +{balanceDiff}
            </Text>
          </Tooltip>
        )}
      </Box>
    </Pressable>
  );
}

export default CoinBalanceHeaderItem;
